import * as React from 'react';

import {
  Badge,
  Button,
  Col,
  FloatingToolbar,
  Menu,
  Pagination,
  Row,
  SearchField,
  TinyTable,
  type PaginationProps,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useSetAtom } from 'jotai';
import {
  LucideEllipsis,
  LucideMail,
  LucidePlusCircle,
  LucideUser,
  LucideUserMinus,
  LucideX,
  LucideXCircle,
} from 'lucide-react';

import { ConfirmationDialog } from '../_shared/ConfirmationDialog';

import { useCancelInvitationMutation } from './_graphql/useCancelInvitationMutation';
import { useCustomerInvitationsQuery } from './_graphql/useCustomerInvitationsQuery';
import { InviteCustomerDialog } from './InviteCustomerDialog';
import { CustomerPortalUserRecord } from './types';

import { SettingsContent } from '~/modules/settings/_components/_shared';
import * as state from '~/modules/settings/_state/customerPortal.state';
import { $openInviteCustomerDialog } from '~/modules/settings/_state/inviteCustomerDialog.state';
import { useSettingsLocation } from '~/modules/settings/_state/settingsModule.state';
import { useTranslations } from '~/modules/settings/i18n/useTranslations';
import useDebouncedValue from '~/shared/hooks/useDebounce';

type CustomerUserRowMenuProps = {
  user: CustomerPortalUserRecord;
  onCancelInvite: () => void;
  onRemoveAccess: () => void;
  onDelete: () => void;
  isDisabled?: boolean;
};

const CustomerUserRowMenu = ({
  user,
  onCancelInvite,
  onRemoveAccess,
  onDelete,
  isDisabled,
}: CustomerUserRowMenuProps) => {
  const t = useTranslations();

  const menuItems = React.useMemo(() => {
    const items: Array<{ label: React.ReactNode; onAction: () => void }> = [];

    if (user.invitationStatus === 'pending') {
      items.push({
        label: (
          <div className="flex items-center gap-2">
            <LucideXCircle className="size-4" />
            {t.customerPortal.users.actions.cancelInvite}
          </div>
        ),
        onAction: onCancelInvite,
      });
    }

    if (user.invitationStatus === 'accepted') {
      items.push({
        label: (
          <div className="flex items-center gap-2">
            <LucideUserMinus className="size-4" />
            {t.customerPortal.users.actions.removeAccess}
          </div>
        ),
        onAction: onRemoveAccess,
      });
    }

    items.push({
      label: (
        <div className="flex items-center gap-2 text-failure">
          <LucideXCircle className="size-4" />
          {t.customerPortal.users.actions.delete}
        </div>
      ),
      onAction: onDelete,
    });

    return items;
  }, [user.invitationStatus, t, onCancelInvite, onRemoveAccess, onDelete]);

  return (
    <Menu
      trigger={
        <Button
          size="icon"
          icon={<LucideEllipsis />}
          variant="ghost"
          color="neutral"
          className="border-neutral/20"
          isDisabled={isDisabled}
        />
      }
      items={menuItems}
      menuClassName="@CustomerUserRowMenu"
    />
  );
};

type CustomerPortalUserStatusBadgeProps = {
  status: 'active' | 'invite_sent';
};

const CustomerPortalUserStatusBadge = ({
  status,
}: CustomerPortalUserStatusBadgeProps) => {
  const t = useTranslations();

  if (status === 'active') {
    return (
      <Badge color="success" size="small">
        {t.customerPortal.userStatus.active}
      </Badge>
    );
  }

  return (
    <Badge
      color="warning"
      size="small"
      className="@CustomerPortalUserStatusBadge"
    >
      {t.customerPortal.userStatus.inviteSent}
    </Badge>
  );
};

export const CustomerPortalUsersTabView = () => {
  const t = useTranslations();
  const toast = useToast();
  const [, setLocation] = useSettingsLocation();

  const cancelInvitationMutation = useCancelInvitationMutation();

  const [selectedIds, setSelectedIds] = useAtom(state.$cpSelectedInvitationIds);
  const [actingOnId, setActingOnId] = React.useState<string | null>(null);

  type PendingAction = {
    type: 'cancel' | 'remove' | 'delete';
    user: CustomerPortalUserRecord;
  };

  const [pendingAction, setPendingAction] =
    React.useState<PendingAction | null>(null);

  const [searchTerm, setSearchTerm] = React.useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

  const [offset, setOffset] = useAtom(state.$cpUsersOffset);
  const [limit, setLimit] = useAtom(state.$cpUsersLimit);

  const openInviteDialog = useSetAtom($openInviteCustomerDialog);

  const {
    data: { users: allUsers = [] } = {},
    isLoading,
    error,
  } = useCustomerInvitationsQuery({
    limit: 1000,
    offset: 0,
  });

  const filteredUsers = React.useMemo(() => {
    if (!debouncedSearchTerm) return allUsers;
    const lowerSearch = debouncedSearchTerm.toLowerCase();
    return allUsers.filter(
      (user) =>
        user.name.toLowerCase().includes(lowerSearch) ||
        user.email.toLowerCase().includes(lowerSearch) ||
        user.company.toLowerCase().includes(lowerSearch),
    );
  }, [allUsers, debouncedSearchTerm]);

  const paginatedUsers = React.useMemo(
    () => filteredUsers.slice(offset, offset + limit),
    [filteredUsers, offset, limit],
  );

  const total = filteredUsers.length;

  const handlePaginationChange: PaginationProps['onChange'] = ({
    limit: newLimit,
    offset: newOffset,
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setOffset(0);
  };

  const handleRowClick = (user: CustomerPortalUserRecord) => {
    setLocation(`/customer-portal/users/${user.id}` as never);
  };

  const handleCancelInvite = (user: CustomerPortalUserRecord) => {
    setPendingAction({ type: 'cancel', user });
  };

  const handleRemoveAccess = (user: CustomerPortalUserRecord) => {
    setPendingAction({ type: 'remove', user });
  };

  const handleDelete = (user: CustomerPortalUserRecord) => {
    setPendingAction({ type: 'delete', user });
  };

  const closePendingAction = () => {
    if (!cancelInvitationMutation.isPending) {
      setPendingAction(null);
    }
  };

  const handleConfirmCancelInvite = async () => {
    if (!pendingAction || pendingAction.type !== 'cancel') return;
    setActingOnId(pendingAction.user.id);
    try {
      await cancelInvitationMutation.mutateAsync({ id: pendingAction.user.id });
      toast.success(t.customerPortal.users.messages.invitationCancelled);
      setPendingAction(null);
    } catch (_err) {
      toast.error(t.customerPortal.users.messages.invitationCancelError);
    } finally {
      setActingOnId(null);
    }
  };

  const handleConfirmRemoveAccess = async () => {
    if (!pendingAction || pendingAction.type !== 'remove') return;
    setActingOnId(pendingAction.user.id);
    try {
      await cancelInvitationMutation.mutateAsync({ id: pendingAction.user.id });
      toast.success(t.customerPortal.users.messages.accessRemoved);
      setPendingAction(null);
    } catch (_err) {
      toast.error(t.customerPortal.users.messages.accessRemoveError);
    } finally {
      setActingOnId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingAction || pendingAction.type !== 'delete') return;
    setActingOnId(pendingAction.user.id);
    try {
      await cancelInvitationMutation.mutateAsync({ id: pendingAction.user.id });
      toast.success(t.customerPortal.users.messages.customerRemoved);
      setPendingAction(null);
    } catch (_err) {
      toast.error(t.customerPortal.users.messages.customerRemoveError);
    } finally {
      setActingOnId(null);
    }
  };

  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedIds),
    [selectedIds],
  );

  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setSelectedIds(Array.from(keys) as string[]);
    },
    [setSelectedIds],
  );

  return (
    <>
      <SettingsContent className="@CustomerPortalUsersTabView h-full">
        <Col className="h-full gap-4">
          <Row gap2>
            <SearchField
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder={t.customerPortal.search.placeholder}
              autoFocus
            />
            <Button
              className="ml-auto"
              icon={<LucidePlusCircle />}
              onPress={openInviteDialog}
            >
              {t.customerPortal.actions.inviteCustomer}
            </Button>
          </Row>

          <TinyTable<CustomerPortalUserRecord>
            rowKey={(row) => row.id}
            selectable
            selectedKeys={selectedKeys}
            onSelectionChange={handleSelectionChange}
            isLoading={isLoading}
            error={error ?? undefined}
            onRowClick={handleRowClick}
            columns={[
              {
                key: 'name',
                label: t.customerPortal.users.table.columns.name,
                icon: LucideUser,
                cell: (row) => (
                  <Col className="gap-0.5">
                    <span>{row.name}</span>
                    <span className="text-xs text-neutral/50">
                      {row.company}
                    </span>
                  </Col>
                ),
              },
              {
                key: 'contactInfo',
                label: t.customerPortal.users.table.columns.contactInfo,
                icon: LucideMail,
                cell: (row) => (
                  <Row className="items-center gap-1.5">
                    <LucideMail className="size-3 text-neutral/50" />
                    <span className="truncate">{row.email || '-'}</span>
                  </Row>
                ),
              },
              {
                key: 'status',
                label: t.customerPortal.users.table.columns.status,
                width: 120,
                cell: (row) => (
                  <CustomerPortalUserStatusBadge status={row.status} />
                ),
              },
              {
                key: 'actions',
                label: '',
                width: 60,
                preventRowClick: true,
                cell: (row) => (
                  <CustomerUserRowMenu
                    user={row}
                    onCancelInvite={() => handleCancelInvite(row)}
                    onRemoveAccess={() => handleRemoveAccess(row)}
                    onDelete={() => handleDelete(row)}
                    isDisabled={actingOnId !== null}
                  />
                ),
              },
            ]}
            data={paginatedUsers}
          />

          <FloatingToolbar visible={selectedIds.length > 0}>
            <div className="flex flex-row items-center justify-between gap-3">
              <div className="flex flex-row items-center gap-2 text-sm">
                <Badge color="primary" size="small">
                  {selectedIds.length}
                </Badge>{' '}
                {t.customerPortal.users.selected}
              </div>
              <div className="flex flex-row items-center gap-2">
                <LucideX
                  className="size-4 cursor-pointer text-neutral/80"
                  onClick={() => setSelectedIds([])}
                />
              </div>
            </div>
          </FloatingToolbar>

          {total > 0 && (
            <Row className="justify-end">
              <Pagination
                limit={limit}
                offset={offset}
                total={total}
                onChange={handlePaginationChange}
              />
            </Row>
          )}
        </Col>
      </SettingsContent>

      <InviteCustomerDialog />

      <ConfirmationDialog
        isOpen={pendingAction?.type === 'cancel'}
        onOpenChange={(open) => {
          if (!open) closePendingAction();
        }}
        title={t.customerPortal.users.dialogs.cancelInvite.title(
          pendingAction?.user.name ?? '',
        )}
        description={t.customerPortal.users.dialogs.cancelInvite.description(
          pendingAction?.user.name ?? '',
        )}
        confirmLabel={t.customerPortal.users.dialogs.cancelInvite.confirm}
        cancelLabel={t.customerPortal.users.dialogs.cancelInvite.discard}
        onConfirm={handleConfirmCancelInvite}
        onCancel={closePendingAction}
        isLoading={cancelInvitationMutation.isPending}
      />

      <ConfirmationDialog
        isOpen={pendingAction?.type === 'remove'}
        onOpenChange={(open) => {
          if (!open) closePendingAction();
        }}
        title={t.customerPortal.users.dialogs.removeAccess.title(
          pendingAction?.user.name ?? '',
        )}
        description={t.customerPortal.users.dialogs.removeAccess.description(
          pendingAction?.user.name ?? '',
        )}
        confirmLabel={t.customerPortal.users.dialogs.removeAccess.remove}
        cancelLabel={t.customerPortal.users.dialogs.removeAccess.discard}
        onConfirm={handleConfirmRemoveAccess}
        onCancel={closePendingAction}
        isLoading={cancelInvitationMutation.isPending}
      />

      <ConfirmationDialog
        isOpen={pendingAction?.type === 'delete'}
        onOpenChange={(open) => {
          if (!open) closePendingAction();
        }}
        title={t.customerPortal.users.dialogs.deleteAccount.title(
          pendingAction?.user.name ?? '',
        )}
        description={t.customerPortal.users.dialogs.deleteAccount.description(
          pendingAction?.user.name ?? '',
        )}
        confirmLabel={t.customerPortal.users.dialogs.deleteAccount.delete}
        cancelLabel={t.customerPortal.users.dialogs.deleteAccount.discard}
        onConfirm={handleConfirmDelete}
        onCancel={closePendingAction}
        isLoading={cancelInvitationMutation.isPending}
      />
    </>
  );
};
